import {
  type IMediaLibUploadingFile,
  MEDIA_LIB_FONT_MAX_SIZE,
  MEDIA_LIB_IMAGE_MAX_SIZE,
  MEDIA_LIB_VIDEO_MAX_SIZE,
  MediaLibCategoryEnum,
  sanitizeSVG,
  sendMediaLibUploadErrorToast,
  sendMediaLibUploadedToast,
  sendMediaLibUploadingToast,
  useMediaLibUploadingStore,
} from "@editor/modules/media-lib";
import { nanoid } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { track } from "@/features/analytics";
import { useAuthStoreShallow } from "@/features/authentication";
import { useUploadMedia } from "@/shared";
import {
  isFontMimeType,
  normalizeFontMimeType,
} from "@/shared/utils/font-mime-type";
import type { IMediaFile, IMediaUploadResponse } from "@/shared/types";
import { useMediaLibOpenFilePicker } from "./use-media-lib-open-file-picker";

const tooBigMsg = (maxSize: number) => {
  return `Maximum file size is ${(maxSize / 1024 ** 2).toFixed(2)}MB. Try to upload a smaller file`;
};

interface IProps {
  accept: string;
  multiple?: boolean;
  moveToCategory?: MediaLibCategoryEnum;

  onUploadStart?: () => void;
  setCategory: (value: MediaLibCategoryEnum) => void;
  setSelectedFolderId: (value: string | null) => void;
  onAddFiles: (value: IMediaFile[]) => void;
  selectedFolderId?: string | null;
}

export const useMediaLibUploadFiles = ({
  accept,
  multiple,
  moveToCategory = MediaLibCategoryEnum.AllFiles,
  onUploadStart,
  onAddFiles,
  setCategory,
  setSelectedFolderId,
  selectedFolderId,
}: IProps) => {
  const setUploadingFiles = useMediaLibUploadingStore(
    (s) => s.setUploadingFiles,
  );

  const { uploadMedia } = useUploadMedia();
  const isAdmin = useAuthStoreShallow((s) => s.isAdmin);
  const { openFilePicker: handleOpenPicker } = useMediaLibOpenFilePicker({
    accept,
    multiple,
  });

  const validateFile = async (
    file: File,
    type: string,
  ): Promise<string | null> => {
    if (type.indexOf("image/") > -1 && file.size > MEDIA_LIB_IMAGE_MAX_SIZE) {
      return tooBigMsg(MEDIA_LIB_IMAGE_MAX_SIZE);
    }
    if (type.indexOf("video/") > -1 && file.size > MEDIA_LIB_VIDEO_MAX_SIZE) {
      return tooBigMsg(MEDIA_LIB_VIDEO_MAX_SIZE);
    }
    if (
      isAdmin &&
      isFontMimeType(type) &&
      file.size > MEDIA_LIB_FONT_MAX_SIZE
    ) {
      return tooBigMsg(MEDIA_LIB_FONT_MAX_SIZE);
    }
    if (
      type.indexOf("image/") === -1 &&
      type.indexOf("video/mp4") === -1 &&
      !isAdmin &&
      isFontMimeType(type)
    ) {
      return `Type ${type} is not supported.`;
    }

    if (type.indexOf("image/svg+xml") > -1) {
      const sanitizedSvg = await sanitizeSVG(file);
      if (!sanitizedSvg) return "Invalid SVG file";
    }

    return null;
  };

  const uploadFiles = async (files: File[]) => {
    if (onUploadStart) onUploadStart();

    const controller = new AbortController();
    const toastId = sendMediaLibUploadingToast({
      onCancel: () => {
        controller.abort();
        setUploadingFiles([]);
      },
    });

    const promises: Promise<IMediaUploadResponse>[] = [];
    const uploadingFiles: IMediaLibUploadingFile[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const type = getMimeType(file);

      const error = await validateFile(file, type);
      if (error) {
        sendMediaLibUploadErrorToast({
          title: `"${file.name}" upload failed`,
          details: error,
        });
      } else {
        uploadingFiles.push({
          id: nanoid(),
          type: type.startsWith("video/")
            ? MediaLibCategoryEnum.Videos
            : MediaLibCategoryEnum.Images,
          index: i,
        });

        promises.push(
          new Promise(async (resolve, reject) => {
            try {
              const mediaFile = await uploadMedia({
                title: file.name,
                blob: file,
                fileType: type,
                signal: controller.signal,
                folderId: selectedFolderId,
              });
              resolve(mediaFile);
            } catch (e) {
              console.log(e);
              if (!controller.signal.aborted) {
                track("failed to upload media file", {
                  file,
                  error: e,
                });
                sendMediaLibUploadErrorToast({
                  title: `"${file.name}" upload failed`,
                });
              }
              reject();
            }
          }),
        );
      }
    }

    setUploadingFiles(uploadingFiles);

    if (selectedFolderId !== MediaLibCategoryEnum.BrandAssets) {
      setSelectedFolderId(selectedFolderId ?? null);
    }

    setCategory(moveToCategory);
    const settled = await Promise.allSettled(promises);

    if (!controller.signal.aborted) {
      let sendSuccessToast = false;
      const uploadedFiles: IMediaUploadResponse[] = [];
      for (const s of settled) {
        if (s.status === "fulfilled") {
          sendSuccessToast = true;
          uploadedFiles.push(s.value);
        }
      }
      onAddFiles(uploadedFiles);
      if (sendSuccessToast) {
        sendMediaLibUploadedToast({ msg: "Uploaded" });
      }
    }
    setUploadingFiles([]);
    toast.dismiss(toastId);
  };

  const openFilePicker = () => {
    handleOpenPicker(uploadFiles);
  };

  return { openFilePicker, uploadFiles };
};

function getMimeType(file: File): string {
  // Browsers report `.woff` as `application/font-woff` and, on macOS, other
  // font extensions as `application/x-font-*`. Canonicalise here so the type we
  // validate, sign the upload against and store is a `font/*` one.
  if (file.type) return normalizeFontMimeType(file.type);
  const ext = file.name.split(".").pop()?.toLowerCase();
  switch (ext) {
    // Fonts
    case "ttf":
      return "font/ttf";
    case "otf":
      return "font/otf";
    case "woff":
      return "font/woff";
    case "woff2":
      return "font/woff2";

    // Images
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "png":
      return "image/png";
    case "gif":
      return "image/gif";
    case "bmp":
      return "image/bmp";
    case "webp":
      return "image/webp";
    case "svgz":
    case "svg":
      return "image/svg+xml";
    case "ico":
      return "image/x-icon";
    case "tif":
    case "tiff":
      return "image/tiff";
    case "avif":
      return "image/avif";
    // Videos
    case "mp4":
      return "video/mp4";
    default:
      return "";
  }
}
